'use client'

import { useEffect, useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'

import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'

import { transformMultiCodeBlocks } from '@common/utils/transformMultiCodeBlocks'

import { transformMarkdownLinks } from '@/lib/github/utils/transformMarkdownLinks'
import { AssetSource } from '@/modules/academy/enums'
import { MARKDOWN_RENDERER_TEST_IDS } from '@/shared/docs-test-ids.const'

import { ImageLightbox } from '../ImageLightbox/ImageLightbox'

import type { MarkdownRendererProps } from './MarkdownRenderer.types'
import { buildMarkdownComponents } from './utils/buildMarkdownComponents'
import { createMarkdownDivHandler } from './utils/createMarkdownDivHandler'
import { transformCollapsibleTags } from './utils/transformCollapsibleTags'
import { transformImagePaths } from './utils/transformImagePaths'
import { transformInfoBoxTags } from './utils/transformInfoBoxTags'

import styles from './MarkdownRenderer.module.scss'

export type { MarkdownComponents } from './MarkdownRenderer.types'
export function MarkdownRenderer({
  content,
  currentPath,
  assetSource = AssetSource.QUICK_START,
}: MarkdownRendererProps) {
  const [isClient, setIsClient] = useState(false)
  const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null)

  useEffect(() => {
    setIsClient(true)
  }, [])

  const { transformed: collapsibleTransformed, collapsibles } = transformCollapsibleTags(content)
  const { transformed: infoBoxTransformed, infoBoxes } = transformInfoBoxTags(collapsibleTransformed)
  const { transformed: multiCodeTransformed, multiCodeBlocks } = transformMultiCodeBlocks(infoBoxTransformed)
  const transformedContent = transformMarkdownLinks(
    transformImagePaths(multiCodeTransformed, assetSource),
    currentPath ?? '',
    assetSource
  )

  const markdownComponents = useMemo(
    () =>
      buildMarkdownComponents({
        isClient,
        onImageClick: (src, alt) => setLightboxImage({ src, alt }),
      }),
    [isClient]
  )

  const divHandler = useMemo(
    () =>
      createMarkdownDivHandler({
        assetSource,
        collapsibles,
        infoBoxes,
        multiCodeBlocks,
        markdownComponents,
      }),
    [assetSource, collapsibles, infoBoxes, markdownComponents, multiCodeBlocks]
  )

  return (
    <div className={styles.container} data-testid={MARKDOWN_RENDERER_TEST_IDS.root}>
      {lightboxImage ? (
        <ImageLightbox src={lightboxImage.src} alt={lightboxImage.alt} onClose={() => setLightboxImage(null)} />
      ) : null}
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          ...markdownComponents,
          div: divHandler,
        }}
      >
        {transformedContent}
      </ReactMarkdown>
    </div>
  )
}
